import { useState, FormEvent, useRef, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import { submitVolunteerApply, uploadVolunteerResume } from '../../../api/dripPlan';

const RESUME_ACCEPT = '.pdf,.doc,.docx';
const RESUME_MAX_MB = 10;

export default function VolunteerApplyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const positionFromUrl = searchParams.get('position') || '';
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    position: positionFromUrl,
    availability: '',
    wechat: '',
    skills: '',
    intro: '',
  });
  const [resumeFilename, setResumeFilename] = useState<string | null>(null);
  const [resumeLocalName, setResumeLocalName] = useState<string | null>(null);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitErrorMsg, setSubmitErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.phone || !formData.position || !formData.availability) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setSubmitErrorMsg('');

    try {
      await submitVolunteerApply({
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        position: formData.position,
        availability: formData.availability,
        wechat: formData.wechat.trim() || undefined,
        skills: formData.skills.trim() || undefined,
        intro: formData.intro.trim() || undefined,
        resumeFilename: resumeFilename || undefined,
      });
      setSubmitStatus('success');
      setFormData({
        name: '',
        phone: '',
        position: '',
        availability: '',
        wechat: '',
        skills: '',
        intro: '',
      });
      setResumeFilename(null);
      setResumeLocalName(null);
    } catch (err) {
      setSubmitStatus('error');
      setSubmitErrorMsg(err instanceof Error ? err.message : '提交失败，请稍后重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name && formData.phone && formData.position && formData.availability;

  // 兜底：若超过 25 秒仍为「提交中」，强制恢复按钮，避免一直卡住
  useEffect(() => {
    if (!isSubmitting) return;
    const t = setTimeout(() => {
      setSubmitStatus('error');
      setSubmitErrorMsg('请求超时，请检查网络后重试');
      setIsSubmitting(false);
    }, 25000);
    return () => clearTimeout(t);
  }, [isSubmitting]);

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative pt-20 md:pt-28 pb-12 md:pb-16 bg-gradient-to-br from-[#2A7A62] to-[#3A9A82]">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center text-white">
            <h1 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4">志愿者报名</h1>
            <p className="text-sm md:text-lg text-white/90 max-w-2xl mx-auto">
              填写报名信息，我们将在3个工作日内与您联系
            </p>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="max-w-2xl mx-auto px-6 py-8 md:py-12 -mt-6">
        <div className="bg-white rounded-xl md:rounded-2xl shadow-xl p-6 md:p-10">
          {submitStatus === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-[#2A7A62] to-[#3A9A82] rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <i className="ri-checkbox-circle-fill text-white text-3xl md:text-4xl"></i>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-[#1E1E1E] mb-3">报名成功！</h3>
              <p className="text-sm md:text-base text-[#6C6C6C] mb-6">
                感谢您的报名，我们将在3个工作日内与您联系
              </p>
              <div className="flex flex-col md:flex-row gap-3 justify-center">
                <button
                  onClick={() => navigate('/volunteer')}
                  className="px-6 py-2.5 bg-[#2A7A62] text-white rounded-lg font-medium hover:bg-[#236854] transition-colors cursor-pointer whitespace-nowrap text-sm md:text-base"
                >
                  返回志愿者招募
                </button>
                <button
                  onClick={() => {
                    setSubmitStatus('idle');
                    setFormData({ name: '', phone: '', position: '', availability: '', wechat: '', skills: '', intro: '' });
                    setResumeFilename(null);
                    setResumeLocalName(null);
                  }}
                  className="px-6 py-2.5 bg-[#F9F9F9] text-[#1E1E1E] rounded-lg font-medium hover:bg-[#E0E0E0] transition-colors cursor-pointer whitespace-nowrap text-sm md:text-base"
                >
                  继续报名
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} id="volunteer-apply-form">
              {/* 提交失败时在表单顶部醒目提示 */}
              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-[#E06B3D]/15 border-2 border-[#E06B3D] rounded-xl flex items-start">
                  <i className="ri-error-warning-fill text-2xl text-[#E06B3D] mr-3 flex-shrink-0 mt-0.5"></i>
                  <div>
                    <div className="font-semibold text-[#E06B3D] text-base">提交失败</div>
                    <div className="text-sm text-[#6C6C6C] mt-1">
                      {submitErrorMsg || '请检查网络连接后重试'}
                    </div>
                    <div className="text-xs text-[#6C6C6C] mt-2">请检查网络或稍后重试，也可联系管理员确认报名接口是否可用。</div>
                  </div>
                </div>
              )}
              {isSubmitting && (
                <div className="mb-4 p-3 bg-[#2A7A62]/10 border border-[#2A7A62] rounded-lg text-sm text-[#2A7A62]">
                  正在提交，请稍候… 若长时间无反应，请检查网络或稍后重试（约 25 秒后按钮可再次点击）。
                </div>
              )}
              <div className="space-y-5 md:space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-[#1E1E1E] mb-2">
                    姓名 <span className="text-[#E06B3D]">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="请输入您的真实姓名"
                    className="w-full h-11 md:h-12 px-4 border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#2A7A62] transition-colors text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1E1E1E] mb-2">
                    联系电话 <span className="text-[#E06B3D]">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="请输入您的手机号码"
                    className="w-full h-11 md:h-12 px-4 border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#2A7A62] transition-colors text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1E1E1E] mb-2">
                    意向岗位 <span className="text-[#E06B3D]">*</span>
                  </label>
                  <select
                    name="position"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    className="w-full h-11 md:h-12 px-4 border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#2A7A62] transition-colors text-sm cursor-pointer"
                    required
                  >
                    <option value="">请选择意向岗位</option>
                    <option value="物资配送志愿者">物资配送志愿者</option>
                    <option value="活动策划志愿者">活动策划志愿者</option>
                    <option value="宣传推广志愿者">宣传推广志愿者</option>
                    <option value="数据管理志愿者">数据管理志愿者</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1E1E1E] mb-2">
                    可服务时间段 <span className="text-[#E06B3D]">*</span>
                  </label>
                  <select
                    name="availability"
                    value={formData.availability}
                    onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                    className="w-full h-11 md:h-12 px-4 border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#2A7A62] transition-colors text-sm cursor-pointer"
                    required
                  >
                    <option value="">请选择可服务时间段</option>
                    <option value="工作日上午（9:00-12:00）">工作日上午（9:00-12:00）</option>
                    <option value="工作日下午（14:00-17:00）">工作日下午（14:00-17:00）</option>
                    <option value="周末全天">周末全天</option>
                    <option value="周末上午">周末上午</option>
                    <option value="周末下午">周末下午</option>
                    <option value="灵活安排">灵活安排</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1E1E1E] mb-2">微信号</label>
                  <input
                    type="text"
                    name="wechat"
                    value={formData.wechat}
                    onChange={(e) => setFormData({ ...formData, wechat: e.target.value })}
                    placeholder="选填，便于我们与您联系"
                    className="w-full h-11 md:h-12 px-4 border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#2A7A62] transition-colors text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1E1E1E] mb-2">技能/经验</label>
                  <textarea
                    name="skills"
                    value={formData.skills}
                    onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                    placeholder="选填，如：活动策划、文案撰写、摄影等，便于匹配岗位"
                    rows={3}
                    className="w-full px-4 py-3 border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#2A7A62] transition-colors text-sm resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1E1E1E] mb-2">个人介绍</label>
                  <textarea
                    name="intro"
                    value={formData.intro}
                    onChange={(e) => setFormData({ ...formData, intro: e.target.value })}
                    placeholder="选填，简单介绍自己或参与志愿服务的初衷"
                    rows={3}
                    className="w-full px-4 py-3 border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#2A7A62] transition-colors text-sm resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#1E1E1E] mb-2">上传简历</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={RESUME_ACCEPT}
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > RESUME_MAX_MB * 1024 * 1024) return;
                      setUploadingResume(true);
                      try {
                        const filename = await uploadVolunteerResume(file);
                        setResumeFilename(filename);
                        setResumeLocalName(file.name);
                      } catch {
                        setResumeFilename(null);
                        setResumeLocalName(null);
                      } finally {
                        setUploadingResume(false);
                        e.target.value = '';
                      }
                    }}
                  />
                  {!resumeFilename ? (
                    <button
                      type="button"
                      disabled={uploadingResume}
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-11 md:h-12 px-4 border border-dashed border-[#E0E0E0] rounded-lg text-[#6C6C6C] text-sm hover:border-[#2A7A62] hover:text-[#2A7A62] transition-colors cursor-pointer disabled:opacity-60"
                    >
                      {uploadingResume ? '上传中...' : '选填，点击上传简历（PDF、Word，不超过 10MB）'}
                    </button>
                  ) : (
                    <div className="flex items-center justify-between w-full h-11 md:h-12 px-4 border border-[#2A7A62] rounded-lg bg-[#2A7A62]/5">
                      <span className="text-sm text-[#1E1E1E] truncate" title={resumeLocalName || resumeFilename}>
                        已上传：{resumeLocalName || resumeFilename}
                      </span>
                      <button
                        type="button"
                        onClick={() => { setResumeFilename(null); setResumeLocalName(null); }}
                        className="text-sm text-[#E06B3D] hover:underline ml-2 flex-shrink-0 cursor-pointer"
                      >
                        移除
                      </button>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={!isFormValid || isSubmitting}
                  className={`w-full h-11 md:h-12 rounded-lg font-semibold transition-all whitespace-nowrap ${
                    isFormValid && !isSubmitting
                      ? 'bg-[#2A7A62] hover:bg-[#236B54] text-white cursor-pointer'
                      : 'bg-[#E0E0E0] text-[#6C6C6C] cursor-not-allowed'
                  }`}
                >
                  {isSubmitting ? '提交中...' : '提交报名'}
                </button>
              </div>
            </form>
          )}

          {submitStatus !== 'success' && (
            <div className="mt-6 p-4 bg-[#F9F9F9] rounded-lg">
              <div className="flex items-start">
                <i className="ri-information-line text-xl text-[#2A7A62] mr-3 mt-0.5"></i>
                <div className="text-sm text-[#6C6C6C]">
                  <div className="font-semibold text-[#1E1E1E] mb-2">温馨提示：</div>
                  <ul className="space-y-1">
                    <li>• 报名成功后，我们将通过电话与您联系确认</li>
                    <li>• 正式服务前需参加志愿者培训</li>
                    <li>• 请确保填写的联系方式准确有效</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Back Button */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-[#2A7A62] font-medium hover:gap-3 transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回上一步
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
}
